// ⬡B:aba_shared.ccwa_core.hooks.viewport_breakpoint:CODE:s18_new_hook:20260430⬡
// Viewport breakpoint hook. Returns {isMobile, isTablet, isDesktop, width, mode}.
//
// Breakpoints:
//   - mobile:  width < 768px   (single-pane chat-first, drawer overlays for left/right)
//   - tablet:  768px <= width < 1024px  (two-pane: center + left, right is a drawer)
//   - desktop: width >= 1024px  (three-pane, current behavior)
//
// Why these numbers:
//   - 768 is the standard iPad portrait / large phone landscape boundary.
//   - 1024 is the iPad landscape / small laptop boundary.
//   - These match the @media breakpoints in aba.css so JS layout decisions
//     and CSS layout decisions stay in lockstep.
//
// SSR safety: returns desktop defaults if window is undefined (during SSR or
// pre-hydration paint). The component re-renders on first client effect.
//
// Listener: uses matchMedia subscriptions (cheaper than resize) plus a single
// resize fallback. Cleans up on unmount.

import { useState, useEffect } from 'react';

const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

function readViewport() {
  if (typeof window === 'undefined') {
    return { width: 1280, isMobile: false, isTablet: false, isDesktop: true, mode: 'desktop' };
  }
  const w = window.innerWidth;
  const isMobile = w <= MOBILE_MAX;
  const isTablet = !isMobile && w <= TABLET_MAX;
  const isDesktop = !isMobile && !isTablet;
  return {
    width: w,
    isMobile,
    isTablet,
    isDesktop,
    mode: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
  };
}

export function useViewportBreakpoint() {
  const [vp, setVp] = useState(() => readViewport());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => setVp(readViewport());

    // Use matchMedia subscriptions for accurate triggers; fall back to resize
    // for browsers that may miss matchMedia events on orientation change.
    const mqMobile = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const mqTablet = window.matchMedia(`(min-width: ${MOBILE_MAX + 1}px) and (max-width: ${TABLET_MAX}px)`);

    const handler = () => update();

    // Modern browsers: addEventListener; legacy Safari: addListener fallback.
    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', handler);
      mqTablet.addEventListener('change', handler);
    } else if (mqMobile.addListener) {
      mqMobile.addListener(handler);
      mqTablet.addListener(handler);
    }

    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);

    // Run once on mount in case initial render produced stale SSR defaults.
    update();

    return () => {
      if (mqMobile.removeEventListener) {
        mqMobile.removeEventListener('change', handler);
        mqTablet.removeEventListener('change', handler);
      } else if (mqMobile.removeListener) {
        mqMobile.removeListener(handler);
        mqTablet.removeListener(handler);
      }
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  return vp;
}
